/**
 * Reading the day plan Alter published to S3.
 *
 * `alter/publish.py`'s `publish_day_plan` is the write side of this door:
 * `plan/` is `study/`'s trust again (see `loadstudy.js`'s module docstring),
 * except the desktop is the only writer here instead of the browser being
 * the only reader of decks and notes -- the browser's grant is `s3:GetObject`
 * on `plan/${identity}/*` and nothing else, no list, no write.
 *
 * That grant is also why this module never lists: `loadStudy` has to
 * discover keys with ListObjectsV2 because it does not know a class's file
 * names in advance, but a day plan's key is derived, not discovered --
 * `plan/{identityId}/{date}.json`, the exact string `publish_day_plan` built
 * it from -- so GetObject is the whole job.
 *
 * The tolerance is `loadstudy.js`'s tolerance, carried over unchanged: this
 * site's job is uploading recordings, and neither a study-material outage
 * nor a missing or broken day plan may cost that. But a day with no plan
 * yet is not even an outage -- it is an ordinary day the desktop has not
 * scheduled -- so unlike `loadStudy`, there is no `ok`/`message` pair to
 * surface here. Every way this can come back empty (no object, a body that
 * does not parse, a body that parses to something other than a plan)
 * collapses to the same `{blocks: []}` a caller cannot tell apart from "not
 * scheduled today," which is the honest answer in all three cases.
 */
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';

/** A *fresh* empty plan every time.
 *
 * This was a shared `const` object returned by reference from all three empty
 * paths, so every caller that ever read an empty day held the same array.
 * Nothing mutates it today, which is exactly why it was worth fixing now: the
 * first caller to sort or push into `blocks` would have poisoned every later
 * empty read for the life of the page, and the reads that broke would be the
 * ones for days with no plan -- the case with no data of its own to make the
 * corruption obvious. */
const empty = () => ({ blocks: [] });

/**
 * @returns {Promise<{blocks: Array}>}
 *
 * Never throws. `s3Impl` stands in for the S3Client the same way `loadStudy`
 * accepts `deps.client` -- a real one is built from `config`/`session` when
 * the caller does not hand one in, and the fake in dayplan.test.js is no
 * wider than that real client's one method it actually calls.
 */
export async function loadDayPlan(config, session, dateIso, s3Impl) {
  const client = s3Impl
    ?? new S3Client({ region: config?.region, credentials: session?.credentials });

  const key = `plan/${session?.identityId}/${dateIso}.json`;

  let text;
  try {
    const object = await client.send(
      new GetObjectCommand({ Bucket: config?.memoBucket, Key: key }),
    );
    text = await object.Body.transformToString();
  } catch {
    // No object at that key is the ordinary case -- most days have no plan.
    // Every other failure (denied, expired, network) lands here too, and
    // gets the same answer: a day plan the page could not read is not
    // distinguishable, from here, from a day plan that was never made.
    return empty();
  }

  let raw;
  try {
    raw = JSON.parse(text);
  } catch {
    return empty();
  }

  if (!raw || !Array.isArray(raw.blocks)) return empty();
  return { blocks: raw.blocks };
}

/**
 * Several days at once, as one flat list of blocks.
 *
 * The calendar shows a week, and a block on Thursday has to be matchable to
 * its assignment just as much as one today. Each day is read independently
 * and a day that fails is simply absent: most days have no plan at all, and
 * one missing day out of seven must not cost the week.
 */
export async function loadDayPlans(config, session, dates, s3Impl) {
  const client = s3Impl
    ?? new S3Client({ region: config?.region, credentials: session?.credentials });

  const days = await Promise.all(
    (dates ?? []).map((date) => loadDayPlan(config, session, date, client)),
  );
  return days.flatMap((day) => day.blocks);
}

/**
 * Blocks keyed by the calendar event Alter created for them.
 *
 * This is the join that lets a tap on a calendar event know which assignment
 * it is for. Blocks without a `gcalEventId` are left out rather than keyed
 * under a null: a plan that was never approved has no event, and keying them
 * all under one absent id would collapse them into a single entry that any
 * unmatched calendar event could then collide with.
 */
export function indexByEvent(blocks) {
  const byEvent = new Map();
  for (const block of blocks ?? []) {
    if (block?.gcalEventId) byEvent.set(block.gcalEventId, block);
  }
  return byEvent;
}
